import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  History,
  FileText,
  User,
  Upload,
  Download,
  Edit,
  Trash2,
  Link2,
  ChevronLeft,
  ChevronRight
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const ACTION_ICONS = {
  create: FileText,
  update: Edit,
  delete: Trash2,
  import: Upload,
  export: Download,
  status_change: History,
  bulk_status_update: History,
  bulk_assign: User,
  create_integration: Link2,
  upload_document: Upload,
  delete_document: Trash2
};

const ACTION_LABELS = {
  create: "Creare",
  update: "Actualizare",
  delete: "Ștergere",
  import: "Import",
  export: "Export",
  status_change: "Schimbare Status",
  bulk_status_update: "Status în masă",
  bulk_assign: "Asignare în masă",
  create_integration: "Configurare Integrare",
  upload_document: "Încărcare Document",
  delete_document: "Ștergere Document",
  update_role: "Actualizare Rol"
};

const ENTITY_LABELS = {
  contract: "Contract",
  contracts: "Contracte",
  user: "Utilizator",
  custom_field: "Câmp Custom",
  document: "Document",
  integration: "Integrare"
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  
  const [filters, setFilters] = useState({
    entity_type: "",
    action: ""
  });

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", pagination.page);
      params.set("limit", pagination.limit);
      if (filters.entity_type) params.set("entity_type", filters.entity_type);
      if (filters.action) params.set("action", filters.action);
      
      const response = await axios.get(`${API_URL}/audit-logs?${params.toString()}`);
      setLogs(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (error) {
      toast.error("Eroare la încărcarea logurilor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [pagination.page, filters]);

  const getActionIcon = (action) => {
    const Icon = ACTION_ICONS[action] || History;
    return <Icon className="h-4 w-4" />;
  };

  const getActionColor = (action) => {
    if (action.includes("delete")) return "bg-red-100 text-red-700";
    if (action.includes("create") || action.includes("import") || action.includes("upload")) return "bg-emerald-100 text-emerald-700";
    if (action.includes("update") || action.includes("status")) return "bg-blue-100 text-blue-700";
    if (action.includes("export")) return "bg-purple-100 text-purple-700";
    return "bg-slate-100 text-slate-700";
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Audit Logs</h1>
        <p className="text-slate-500 mt-1">
          Istoric al tuturor acțiunilor din sistem
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Select
                value={filters.entity_type || "all"}
                onValueChange={(value) => {
                  setFilters({ ...filters, entity_type: value === "all" ? "" : value });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <SelectTrigger data-testid="entity-filter">
                  <SelectValue placeholder="Toate entitățile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate entitățile</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="contracts">Contracte (Bulk)</SelectItem>
                  <SelectItem value="user">Utilizator</SelectItem>
                  <SelectItem value="document">Document</SelectItem>
                  <SelectItem value="custom_field">Câmp Custom</SelectItem>
                  <SelectItem value="integration">Integrare</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1">
              <Select
                value={filters.action || "all"}
                onValueChange={(value) => {
                  setFilters({ ...filters, action: value === "all" ? "" : value });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <SelectTrigger data-testid="action-filter">
                  <SelectValue placeholder="Toate acțiunile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate acțiunile</SelectItem>
                  <SelectItem value="create">Creare</SelectItem>
                  <SelectItem value="update">Actualizare</SelectItem>
                  <SelectItem value="delete">Ștergere</SelectItem>
                  <SelectItem value="import">Import</SelectItem>
                  <SelectItem value="export">Export</SelectItem>
                  <SelectItem value="status_change">Schimbare Status</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button
              variant="outline"
              onClick={() => {
                setFilters({ entity_type: "", action: "" });
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              Resetează
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Istoric Acțiuni</CardTitle>
          <CardDescription>
            {pagination.total} înregistrări găsite
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <History className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">Nu există loguri</p>
            </div>
          ) : (
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Ora</TableHead>
                    <TableHead>Acțiune</TableHead>
                    <TableHead>Entitate</TableHead>
                    <TableHead>Utilizator</TableHead>
                    <TableHead>Detalii</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => {
                    const ts = log.created_at || log.timestamp;
                    const d = ts ? new Date(ts) : null;
                    const tsDisplay = d && !isNaN(d.getTime()) ? d.toLocaleString("ro-RO") : "—";
                    const userDisplay = log.user?.name || log.user?.email || log.user_name || "Sistem";
                    return (
                    <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                      <TableCell className="whitespace-nowrap text-sm" data-testid={`audit-ts-${log.id}`}>
                        {tsDisplay}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getActionColor(log.action)} flex items-center gap-1 w-fit`}>
                          {getActionIcon(log.action)}
                          <span>{ACTION_LABELS[log.action] || log.action}</span>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <span className="font-medium">
                            {ENTITY_LABELS[log.entity_type] || log.entity_type}
                          </span>
                          {log.entity_id && (
                            <p className="text-slate-500 text-xs font-mono">
                              {String(log.entity_id).slice(0, 8)}...
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="h-4 w-4 text-slate-600" />
                          </div>
                          <span className="text-sm">{userDisplay}</span>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <div className="text-xs text-slate-500">
                            {Object.entries(log.details).slice(0, 3).map(([key, value]) => (
                              <div key={key}>
                                <span className="font-medium">{key}:</span>{" "}
                                {typeof value === "object" ? JSON.stringify(value) : String(value).slice(0, 30)}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.total > pagination.limit && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Afișare {((pagination.page - 1) * pagination.limit) + 1} -{" "}
            {Math.min(pagination.page * pagination.limit, pagination.total)} din{" "}
            {pagination.total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              data-testid="prev-page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-slate-700 px-2">
              Pagina {pagination.page} din {Math.ceil(pagination.total / pagination.limit)}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page * pagination.limit >= pagination.total}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              data-testid="next-page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
